import type { ReactNode } from 'react';

/**
 * App-agnostic form/dialog primitives shared by the desktop and web clients.
 * They render against the shared design-system classes in
 * `@kolectra/shared/styles.css` (.dialog/.scrim/.field/.chiptog) and carry no
 * domain or icon dependencies — the caller passes the icon as a node.
 */

/** A centered modal dialog. */
export function Modal({
  title,
  subtitle,
  icon,
  zone = 'meta',
  onClose,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  icon: ReactNode;
  zone?: 'meta' | 'data';
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <div className="scrim" onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dh">
          <div className={'wi' + (zone === 'data' ? ' data' : '')}>{icon}</div>
          <div>
            <h3>{title}</h3>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button type="button" className="closex" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="db">{children}</div>
        <div className="df">{footer}</div>
      </div>
    </div>
  );
}

/** A set of toggleable chips for multi-selects (roles, permissions, languages). */
export function ChipToggle({
  label,
  options,
  selected,
  onToggle,
}: {
  label?: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  return (
    <div className="field">
      {label && <label>{label}</label>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.length === 0 && <span className="mini">none available</span>}
        {options.map((o) => (
          <button
            type="button"
            key={o.value}
            className={'chiptog' + (selected.includes(o.value) ? ' on' : '')}
            onClick={() => onToggle(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** A labelled field wrapper, with an optional help tooltip dot. */
export function Field({
  label,
  hint,
  help,
  children,
}: {
  label: string;
  hint?: string;
  help?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label>
        {label}
        {help && (
          <span className="idot" tabIndex={0} title={help}>
            ?
          </span>
        )}
        {hint && <span className="hint"> {hint}</span>}
      </label>
      {children}
    </div>
  );
}

/** Add/remove a value from a string array (for ChipToggle handlers). */
export const toggled = (arr: string[], v: string): string[] =>
  arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

/** Generate a reasonably strong random password (crypto when available). */
export function generatePassword(length = 14): string {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%^&*';
  const out: string[] = [];
  const c = globalThis.crypto;
  if (c?.getRandomValues) {
    const arr = new Uint32Array(length);
    c.getRandomValues(arr);
    for (let i = 0; i < length; i++) out.push(chars[arr[i]! % chars.length]!);
  } else {
    for (let i = 0; i < length; i++) out.push(chars[Math.floor(Math.random() * chars.length)]!);
  }
  return out.join('');
}
